import React from "react";
import { Box, LinearProgress, Skeleton, Tooltip, Typography } from "@mui/material";
import { useIntl } from "react-intl";
import cmmsMessages from "../cmmsMessages";
import { EMPTY_VALUE, formatQuantity } from "../cmmsFormat";
import {
  ToolTriggerBasis,
  buildToolServiceSummary,
  getToolServiceLevelColor,
  getToolServiceLevelLabel,
} from "../toolServiceStatus";

/*
 * How far one tool has run against its service schedule.
 *
 * Presentational only — it takes the reading it is given and never fetches. That is what lets
 * the tools grid, the history panel and the machine page badge share it
 * without any of them owning the read.
 *
 * Shaped after ScheduleRow in AssetUsageTab, deliberately: the two answer the same
 * question about different subjects, and a user who has learned to read one should not have to
 * learn the other.
 */
export default function ToolServiceStatusCell({ status, isLoading = false }) {
  const intl = useIntl();

  if (isLoading) {
    return <Skeleton variant="rounded" height={8} width="70%" animation="wave" />;
  }

  /*
   * An em dash, not a blank. Most rows in a 1 465-row tool register have no usage schedule, and
   * a blank cell reads as a rendering fault where a dash reads as "nothing watches this".
   */
  if (!status) {
    return (
      <Tooltip title={intl.formatMessage(cmmsMessages.toolStatusNotWatched)} arrow>
        <Typography variant="body2" sx={{ color: "text.disabled" }}>
          {EMPTY_VALUE}
        </Typography>
      </Tooltip>
    );
  }

  const isRunningTime = status.triggerBasis === ToolTriggerBasis.RunningTime;
  const percent = Number(status.percentConsumed) || 0;
  const color = getToolServiceLevelColor(status.level);

  /*
   * The bar is clamped, the number is not. An overdue tool should read how far past it is —
   * the same split ScheduleRow makes, and what the server means by leaving Remaining
   * deliberately unclamped ("An overdue tool should read how far past it is").
   */
  const barValue = Math.min(Math.max(percent, 0), 100);

  const ratio = intl.formatMessage(
    isRunningTime ? cmmsMessages.toolStatusRatioHours : cmmsMessages.toolStatusRatioCycles,
    {
      consumed: formatQuantity(intl, status.consumed, isRunningTime ? 1 : 0),
      interval: formatQuantity(intl, status.interval, 0),
    },
  );

  /*
   * The schedule title leads the tooltip because a tool can be watched by two schedules — one
   * in hours, one in cycles — and the cell can only show the worse of them. Without the title
   * there is no way to tell which threshold the number belongs to.
   *
   * The body comes from buildToolServiceSummary, shared with the machine page's badge.
   */
  const tooltip = [
    status.scheduleTitle,
    getToolServiceLevelLabel(status.level, intl),
    buildToolServiceSummary(status, intl),
  ]
    .filter(Boolean)
    .join(" · ");

  return (
    <Tooltip title={tooltip} arrow>
      <Box sx={{ width: "100%", minWidth: 0, py: 0.5 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <LinearProgress
            variant="determinate"
            value={barValue}
            sx={{
              flex: 1,
              height: 8,
              borderRadius: 1,
              backgroundColor: "action.hover",
              "& .MuiLinearProgress-bar": { backgroundColor: color },
            }}
          />
          <Typography variant="body2" sx={{ color, fontWeight: 700, flexShrink: 0 }}>
            {intl.formatNumber(Math.round(percent))}%
          </Typography>
        </Box>
        <Typography
          variant="caption"
          sx={{
            color: "text.secondary",
            display: "block",
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {ratio}
        </Typography>
      </Box>
    </Tooltip>
  );
}
